import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateQuote, useLot, useQuote, useVehicles } from '../api/hooks';
import { TopBar } from '../components/chrome';
import { IconAlert } from '../components/icons';
import {
  Cta,
  ErrorState,
  LinkButton,
  Loading,
  Muted,
  Note,
  Row,
  Screen,
  SecondaryButton,
} from '../components/ui';
import { useApiError } from '../i18n/useApiError';
import { countdown, durationLabel, plateDisplay, usd } from '../lib/format';
import { lotClockLabel, lotExpiryLabel, resolveLotTimeLanguage } from '../lib/lot-time';
import { useCountdown } from '../lib/use-countdown';

export function ConfirmPage() {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();
  const { lotId } = useParams({ from: '/lot/$lotId/confirm' });
  const { quoteId } = useSearch({ from: '/lot/$lotId/confirm' }) as { quoteId: string };
  const navigate = useNavigate();
  const quoteQuery = useQuote(quoteId);
  const quote = quoteQuery.data?.quote;
  const lot = useLot(quote?.lotId);
  const vehicles = useVehicles();
  const createQuote = useCreateQuote();
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [startAnother, setStartAnother] = useState(false);
  const [duplicateAck, setDuplicateAck] = useState(false);
  const remaining = useCountdown(
    quote?.expiresAt ?? '1970-01-01T00:00:00.000Z',
    quote
      ? Math.max(
          0,
          Math.floor((new Date(quote.expiresAt).getTime() - new Date(quote.createdAt).getTime()) / 1000),
        )
      : 0,
  );

  if (quoteQuery.isPending)
    return (
      <Page>
        <Loading label={t('confirm.locking')} />
      </Page>
    );
  if (quoteQuery.isError || !quote) {
    return (
      <Page>
        <ErrorState message={t('confirm.quoteFailed')} onRetry={() => void quoteQuery.refetch()} />
      </Page>
    );
  }
  const vehicle = vehicles.data?.vehicles.find((item) => item.id === quote.vehicleId);
  if (!vehicle)
    return (
      <Page>
        <Loading />
      </Page>
    );

  const refresh = async () => {
    setRefreshError(null);
    try {
      const next = await createQuote.mutateAsync({
        lotId: quote.lotId,
        vehicleId: vehicle.id,
        spaceNumber: quote.spaceNumber,
        durationMinutes: quote.requestedMinutes,
      });
      await navigate({
        to: '/lot/$lotId/confirm',
        params: { lotId },
        search: { quoteId: next.quote.id },
        replace: true,
      });
    } catch (cause) {
      setRefreshError(describeError(cause, 'confirm.quoteFailed'));
    }
  };

  if (quote.status !== 'valid' || remaining <= 0) {
    return (
      <Page>
        <div className="flex flex-col gap-4 py-16">
          <IconAlert className="h-10 w-10 text-warning" />
          <h1 className="text-3xl font-bold">{t('confirm.expiredTitle')}</h1>
          <Muted>{t('confirm.expiredBody')}</Muted>
          {refreshError ? <p className="text-sm text-danger">{refreshError}</p> : null}
          <Cta label={t('confirm.refresh')} loading={createQuote.isPending} onClick={() => void refresh()} />
        </div>
      </Page>
    );
  }

  const expectedExpiry = new Date(new Date(quote.startAt).getTime() + quote.entitlementMinutes * 60_000);
  const duplicate = quoteQuery.data?.duplicateWarning;
  const lang = resolveLotTimeLanguage(i18n.language);
  // The quote itself carries no timezone; the lot detail it was priced for
  // does. Until that payload lands, times fall back to the device zone with
  // its own honest Intl label — never a hardcoded lot zone.
  const timezone = lot.data?.timezone;

  return (
    <>
      <TopBar title={t('confirm.title')} wide />
      {/* Wide desktop page (review §4.13). The min-h + flex-1 spacer exist to
          pin the phone's sticky footer to the viewport bottom; at lg the
          footer is static right after the summary (see below), so the
          stretching is switched off and normal bottom padding returns. */}
      <Screen wide className="min-h-[calc(100dvh-52px)] pb-0 lg:min-h-0 lg:pb-12">
        <div className="flex items-center gap-3 rounded-lg border border-hairline p-3">
          <span className="h-14 w-14 shrink-0 rounded-md bg-surface-strong" />
          <span className="flex flex-col gap-0.5">
            <span className="text-base font-semibold">{lot.data?.name ?? ''}</span>
            {/* Lot-name convention (review §4.12): official name primary, the
                Chinese alias leads the muted secondary line — same as the home
                list rows and the lot page. */}
            <span className="text-xs text-muted">
              {[lot.data?.nameZh, lot.data?.address].filter(Boolean).join(' · ')}
            </span>
          </span>
        </div>

        <div className="flex flex-col items-center gap-1.5 rounded-lg border-2 border-ink p-4">
          <Muted>{t('confirm.confirmPlate')}</Muted>
          <span className="num text-4xl font-bold tracking-[4px]">{plateDisplay(vehicle.plate)}</span>
          <Muted>{t('confirm.plateWarning', { state: vehicle.state })}</Muted>
        </div>

        {duplicate ? (
          /* The review order (lot -> plate -> expiry) also governs what the
             driver must be able to check before knowingly paying twice. */
          <div className="flex flex-col gap-2.5 rounded-lg border-[1.5px] border-warning p-3">
            <p className="text-[13px] leading-relaxed text-warning">{t('confirm.duplicate')}</p>
            <div className="flex flex-col gap-1.5 border-t border-hairline pt-2.5">
              <Row label={t('confirm.duplicateLot')} value={duplicate.lot.name} />
              <Row label={t('confirm.duplicatePlate')} value={plateDisplay(duplicate.plate)} mono />
              <Row
                label={t('confirm.duplicateRemaining')}
                value={countdown(duplicate.remainingSeconds)}
                mono
              />
              <Row
                label={t('confirm.duplicateEndsAt')}
                value={lotExpiryLabel(duplicate.expiresAt, duplicate.lot.timezone, lang, t)}
                mono
              />
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-2.5">
          {quote.spaceNumber !== null ? (
            <Row label={t('confirm.spaceNumber')} value={quote.spaceNumber.toString()} mono />
          ) : null}
          <Row label={t('confirm.requestedDuration')} value={durationLabel(quote.requestedMinutes, t)} />
          {quote.requestedMinutes !== quote.entitlementMinutes ? (
            <Row label={t('confirm.validDuration')} value={durationLabel(quote.entitlementMinutes, t)} />
          ) : null}
          <Row label={t('confirm.endsAt')} value={lotExpiryLabel(expectedExpiry, timezone, lang, t)} mono />
        </div>

        <div className="flex flex-col gap-3 rounded-lg bg-surface-muted p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] text-muted">{t('confirm.amountDue')}</span>
            <span className="num text-3xl font-bold">{usd(quote.totalCents)}</span>
          </div>
        </div>

        <Note>{t('confirm.footnote', { remaining: countdown(remaining) })}</Note>
        <div className="flex-1 lg:hidden" />
        {/* Sticky pay bar on phones; static at lg so the amount recap and the
            pay button stay adjacent to the summary instead of being orphaned
            at the bottom of a tall desktop viewport (review §4.13). */}
        <div className="sticky bottom-0 -mx-[18px] flex flex-col gap-2 border-t border-hairline bg-white px-[18px] pb-4 pt-3 lg:static">
          <div className="num flex justify-between gap-3 text-[11px] text-muted">
            <span>{t('confirm.generatedAt', { time: lotClockLabel(quote.createdAt, timezone, lang) })}</span>
            <span>
              #{quote.id.slice(0, 8).toUpperCase()} · {countdown(remaining)}
            </span>
          </div>
          {/* While the duplicate warning stands there is no plain pay path:
              extending leads, starting another is an outline step, and the pay
              button only arms behind the explicit acknowledgement. */}
          {duplicate ? (
            startAnother ? (
              <>
                <label className="flex min-h-11 items-center gap-2.5 text-[13px] leading-relaxed">
                  <input
                    type="checkbox"
                    checked={duplicateAck}
                    onChange={(event) => setDuplicateAck(event.target.checked)}
                    className="h-[18px] w-[18px] shrink-0 accent-ink"
                  />
                  <span>{t('confirm.duplicateAck')}</span>
                </label>
                <Cta
                  label={t('confirm.pay')}
                  note={t('confirm.payNote', { amount: usd(quote.chargeCents) })}
                  disabled={!duplicateAck}
                  onClick={() =>
                    void navigate({ to: '/pay', search: { quoteId: quote.id, kind: quote.kind } })
                  }
                />
                <LinkButton
                  label={t('common.cancel')}
                  onClick={() => {
                    setStartAnother(false);
                    setDuplicateAck(false);
                  }}
                />
              </>
            ) : (
              <>
                <Cta
                  tone="accent"
                  label={t('confirm.duplicateExtend')}
                  note={t('confirm.duplicateExtendNote', {
                    remaining: countdown(duplicate.remainingSeconds),
                  })}
                  onClick={() =>
                    void navigate({ to: '/sessions/$sessionId', params: { sessionId: duplicate.id } })
                  }
                />
                <SecondaryButton
                  label={t('confirm.duplicateStartAnother')}
                  onClick={() => setStartAnother(true)}
                />
              </>
            )
          ) : (
            <Cta
              label={t('confirm.pay')}
              note={t('confirm.payNote', { amount: usd(quote.chargeCents) })}
              onClick={() => void navigate({ to: '/pay', search: { quoteId: quote.id, kind: quote.kind } })}
            />
          )}
        </div>
      </Screen>
    </>
  );
}

function Page({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <>
      {/* Same wide chrome as the loaded state so the bar and column widths do
          not jump between loading/expired and the real summary. */}
      <TopBar title={t('confirm.title')} wide />
      <Screen wide>{children}</Screen>
    </>
  );
}
